// Round 1, lane C — the write shape and its validity (docs/briefs/ROUND1-SPEC-2026-09-05.md
// sections F.1, F.3 and F.4). Three pure functions:
//   validateBranch — the port of vendor/ghost-ship/editor-api/src/controllers/story_branch.js:68-98
//                    (the same read of stepTypeId, the same truthiness on `answers`, the same order
//                    of checks) plus the hub rule the engine enforces on create and update
//                    (story_branch.js:337-338, :429-430). The engine's own message is the opaque
//                    "Error in step types combination"; the reasons here are Basil's (F.4).
//                    The truth table is exact for CANONICAL step types only — a decimal integer,
//                    written as a number or as its plain decimal string. The engine reads the field
//                    with a radix-less parseInt, which also accepts spellings decimal reading does
//                    not ('0x3' is 3 there, NaN here) and accepts trailing junk ('3.0' is 3 there).
//                    Rather than guess which reading a future engine keeps, a non-canonical spelling
//                    is refused outright (BAR-R1-08, reports/round1-bar-review-2026-09-05/): the
//                    compiler never emits one, so nothing Basil writes is affected. Measured against
//                    that review's own 4,599-row matrix (write-validation.cjs:12-24): 4,543 rows
//                    agree exactly; the 56 that carry the leading-zero spelling '03' are refused
//                    here and accepted there — by intent, and never the other way round.
//   shapeForWrite  — from a fixture-shaped branch (the read shape: answers[] on every step, the
//                    read-only branch keys) to the branch the whole-branch PUT sends (F.3). Its
//                    per-step half, shapeStepForWrite, replaces the frozen planner's
//                    structuralWriteStep (plan.mjs:254-265) and is identical to it on every step the
//                    pinned plan vectors carry (the extra strips only touch engine fields a step of
//                    another type never has).
//   splitScene     — where a scene's blocks are cut into node parts (F.1): a Choice, Check or Hub
//                    block is alone in its part; everything else runs together. Pure: it says where
//                    to cut, the compiler (lane B) makes the nodes and routes the parts.
// The dice notation the engine's parser certainly accepts, for the spell check:
// cspell:ignore NdM
import type {
  Block,
  Branch,
  Step,
  ValidationVerdict,
  WriteBranch,
  WriteShapeStep,
} from '../../dorian-shared/types/episodeScript';
import { deepClone } from './deepClone';

/** F.4: the exact reasons `validateBranch` gives, by the engine line each one ports. */
export const VALIDATION_REASONS = Object.freeze({
  /** story_branch.js:91 — more than one step of type 3 or 11. */
  manyChoices: 'a scene can have just one choice or check in it',
  /** story_branch.js:92 — a step of type 3 or 11 with company. */
  notAlone: 'put a choice or a check in a scene of its own',
  /** story_branch.js:93 — a regular node holding a choice or check and a node route. */
  regularRoute: 'a scene with a choice or a check ends there, and each answer carries the reader on',
  /** story_branch.js:95 — no choice or check, and the first step carries `answers`. */
  answers: 'Only a choice can carry answers.',
  /** story_branch.js:96 — no choice or check, and the first step carries `check` or `switch`. */
  check: 'only a check line can test what the story remembers',
  /** story_branch.js:337-338 and :429-430 — a hub step with answers is refused on create and update. */
  hub: 'a hub is a scene readers come back to, and its cards carry them on',
  /** BAR-R1-08 — a step type the engine's radix-less parseInt would read differently, or not at all. */
  stepType: "Basil can't tell what kind of line this is",
} as const);

/** F.4: the dice notation the engine's parser certainly accepts (NdM with an optional ±K); the port is conservative (section L). */
export const DICE_NOTATION = /^\d*d\d+([+-]\d+)?$/i;

/** The step types the engine tallies as "choice" (story_branch.js:73). */
const CHOICE_LIKE_TYPES = [3, 11];
const CHOICE_STEP_TYPE = 3;
const CHECK_STEP_TYPE = 11;
const REMEMBER_STEP_TYPE = 12;
const ACHIEVE_STEP_TYPE = 13;
const VIDEO_STEP_TYPE = 14;
const RANDOM_STEP_TYPE = 15;
const HUB_STEP_TYPE = 16;

/** The engine's own read of a step's type (story_branch.js:73, :335): parseInt, radix 10. */
function stepTypeOf(step: { stepTypeId?: unknown }): number {
  return parseInt(String(step.stepTypeId), 10);
}

/** F.4, BAR-R1-08: the one spelling of a step type this port and the engine certainly read alike. */
const CANONICAL_STEP_TYPE = /^-?(?:0|[1-9][0-9]*)$/;

/**
 * BAR-R1-08: is the step's type spelled the way both readers agree on — a whole number, or the plain
 * decimal string of one ('3', never '0x3', ' 3', '3.0' or the leading-zero '03')? A step with no type
 * at all keeps the engine's own answer (both sides read NaN and count it as no choice), so it is not
 * a spelling to refuse; `stepTypeOf` may be trusted for every step this returns true for.
 */
function hasCanonicalStepType(step: { stepTypeId?: unknown }): boolean {
  const value = step.stepTypeId;
  if (value === undefined || value === null) return true;
  if (typeof value === 'number') return Number.isInteger(value);
  return typeof value === 'string' && CANONICAL_STEP_TYPE.test(value);
}

/** F.4, the dice row: the notation the engine would parse, or null when there is nothing to parse. */
function refusedDiceNotation(step: WriteShapeStep): string | null {
  const roll = step.roll as { type?: unknown; diceNotation?: unknown } | undefined;
  if (!roll || roll.type !== 'dice') return null;
  const notation = String(roll.diceNotation ?? '');
  if (notation.trim() === '') return null;
  return DICE_NOTATION.test(notation.trim()) ? null : notation;
}

const ok: ValidationVerdict = Object.freeze({ ok: true, reason: null });

function refused(reason: string): ValidationVerdict {
  return { ok: false, reason };
}

/**
 * story_branch.js:68-98 with the same truth table on canonical step types, then the hub rule.
 * `branch.steps` is read the way the engine reads `data.steps` (a missing list is valid), so a
 * document that is not quite the write shape still gets the engine's answer. A step whose type is
 * spelled some other way is refused before the tally rather than read a second way (BAR-R1-08).
 */
export function validateBranch(branch: WriteBranch): ValidationVerdict {
  const { steps } = branch as { steps?: unknown };
  if (!Array.isArray(steps)) return ok;
  const list = steps as WriteShapeStep[];
  let choice = 0;
  for (let index = 0; index < list.length; index += 1) {
    const step = list[index];
    if (!hasCanonicalStepType(step)) return refused(VALIDATION_REASONS.stepType);
    if (CHOICE_LIKE_TYPES.includes(stepTypeOf(step))) choice += 1;
    const dice = refusedDiceNotation(step);
    if (dice !== null) return refused(`the episode may not accept the dice roll “${dice}”`);
  }
  if (choice > 1) return refused(VALIDATION_REASONS.manyChoices);
  if (choice > 0 && list.length !== 1) return refused(VALIDATION_REASONS.notAlone);
  if (choice > 0 && branch.stepTypeMode === 'regular' && branch.gotoBranchId) return refused(VALIDATION_REASONS.regularRoute);
  if (list.length === 0) return ok;
  const first = list[0] as { answers?: unknown; check?: unknown; switch?: unknown };
  if (choice === 0 && first.answers) return refused(VALIDATION_REASONS.answers);
  if (choice === 0 && (first.check || first.switch)) return refused(VALIDATION_REASONS.check);
  const hubWithAnswers = list.some((step) => stepTypeOf(step) === HUB_STEP_TYPE
    && Array.isArray(step.answers) && step.answers.length > 0);
  if (hubWithAnswers) return refused(VALIDATION_REASONS.hub);
  return ok;
}

/** A synthetic id the compiler minted (a negative number, or its string spelling): never sent. */
function isSyntheticId(id: unknown): boolean {
  if (typeof id === 'number') return id < 0;
  return /^-\d+$/.test(String(id));
}

/**
 * F.3, per step: drop `wordCount`, `branchId` and a negative `id`; keep `answers` only on a Choice
 * (Addendum 1 A.2: an empty array is truthy for validateBranch), `check`/`switch` only on a Check,
 * `action` only on a Remember, `roll` only on a Random, `hubSettings`/`hubOptions` only on a Hub,
 * `achievementId`/`achievementDescription` only on an Achieve, `video` only on a Video. Never
 * dropped: `stepAudios`, `data`, `speakerMemoryId`, `characterPlacementSide`, `subType`,
 * `characterRelationship`, `currencyToggleSettings`. Key order is the step's own.
 */
export function shapeStepForWrite(step: Step): WriteShapeStep {
  const result = deepClone(step) as unknown as Record<string, unknown>;
  const type = stepTypeOf(step);
  delete result.wordCount;
  delete result.branchId;
  if (isSyntheticId(result.id)) delete result.id;
  if (type !== CHOICE_STEP_TYPE) delete result.answers;
  if (type !== CHECK_STEP_TYPE) {
    delete result.check;
    delete result.switch;
  }
  if (type !== REMEMBER_STEP_TYPE) delete result.action;
  if (type !== RANDOM_STEP_TYPE) delete result.roll;
  if (type !== HUB_STEP_TYPE) {
    delete result.hubSettings;
    delete result.hubOptions;
  }
  if (type !== ACHIEVE_STEP_TYPE) {
    delete result.achievementId;
    delete result.achievementDescription;
  }
  if (type !== VIDEO_STEP_TYPE) delete result.video;
  return result as WriteShapeStep;
}

/**
 * F.3, the branch: the read-only keys (`location_title`, `location`, `bonus_content`, `links`,
 * `traits`, `answers`, `type`, `steps_count`, `words_count`, `check`, `switch`, `uuid`, `x`, `y`,
 * `bgColor`) are left behind by construction; `id` is kept unless synthetic; `stepTypeMode` is
 * `regular` when absent. Raises nothing.
 */
export function shapeForWrite(branch: Branch): WriteBranch {
  const body: Omit<WriteBranch, 'id'> = {
    title: String(branch.title ?? ''),
    description: String(branch.description ?? ''),
    gotoBranchId: branch.gotoBranchId ?? null,
    locationId: branch.locationId ?? null,
    stepTypeMode: branch.stepTypeMode === 'elimination' ? 'elimination' : 'regular',
    pos: branch.pos ?? null,
    steps: (branch.steps ?? []).map(shapeStepForWrite),
  };
  if (branch.id === undefined || branch.id === null || isSyntheticId(branch.id)) return body;
  return { id: branch.id, ...body };
}

// F.1: how a block counts when a scene is cut. The Round 1 block types lane A adds (video, achieve,
// roll, legacy, the `> check:` envelope, `> end` as a type-5 step) are named here by their `type`
// and `directive` strings so the cut needs no change when they land; a block this module does not
// know is not a step and travels with the run it sits in.
const STEP_BLOCK_TYPES = new Set(['character', 'narration', 'video', 'achieve', 'roll', 'legacy']);
const STEP_DIRECTIVES = new Set(['remember', 'forget', 'end']);
const ISOLATED_BLOCK_TYPES = new Set(['choice', 'hub']);
const ISOLATED_DIRECTIVES = new Set(['if', 'check']);

function directiveOf(block: Block): string | null {
  return block.type === 'directive' ? block.directive : null;
}

/** F.1: a block that compiles to a step (of any type). */
export function isNodeStepBlock(block: Block): boolean {
  const directive = directiveOf(block);
  if (directive !== null) return STEP_DIRECTIVES.has(directive) || ISOLATED_DIRECTIVES.has(directive);
  return STEP_BLOCK_TYPES.has(block.type) || ISOLATED_BLOCK_TYPES.has(block.type);
}

/** F.1: a block whose step must be alone in its node — a Choice, a Check (either spelling) or a Hub. */
export function isIsolatedBlock(block: Block): boolean {
  const directive = directiveOf(block);
  if (directive !== null) return ISOLATED_DIRECTIVES.has(directive);
  return ISOLATED_BLOCK_TYPES.has(block.type);
}

/**
 * F.1: cut a scene's blocks into parts so that every part is either exactly one Choice, Check or
 * Hub block (with the non-step blocks that sit beside it) or a run of other blocks. Cuts fall
 * before and after each isolated block. Non-step blocks (`> go to:`, notes) belong to the part
 * they sit in: a run of them never makes a part of its own — it leads the next part or, at the
 * end of the scene, trails the last one. A scene with no step block at all is one part (possibly
 * empty: the engine accepts an empty node).
 */
export function splitScene(blocks: Block[]): Block[][] {
  const parts: Block[][] = [];
  let run: Block[] = [];
  const runHasStep = (): boolean => run.some(isNodeStepBlock);
  blocks.forEach((block) => {
    if (!isIsolatedBlock(block)) {
      run.push(block);
      return;
    }
    if (runHasStep()) {
      parts.push(run);
      run = [];
    }
    parts.push([...run, block]);
    run = [];
  });
  if (runHasStep() || parts.length === 0) {
    parts.push(run);
  } else if (run.length) {
    parts[parts.length - 1].push(...run);
  }
  return parts;
}
